<template>
  <v-container fluid class="pa-0">
    <v-dialog v-model="showInviteModal" max-width="600px">
      <v-card>
        <v-toolbar color="secondary" dark>
          <h2>New Invite</h2>
        </v-toolbar>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col>
                <v-text-field label="Email" required v-model="inviteEmail"></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
              <v-text-field
                readonly
                :value="inviteLink()"
                filled
                label="Share Link"
                append-icon="mdi-clipboard-text"
                @click:append="copyInvite"
              ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <canvas id="qrcanvas"></canvas>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="showInviteModal = false" v-if="!inviteSending">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="sendInvite" :loading="inviteSending">Send</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showScanModal" max-width="600px" @input="scanQRClose">
      <v-container fluid>
        <v-row>
          <v-alert type="error" :value="qrValidationError.length > 0">
            {{ qrValidationError }}
          </v-alert>
        </v-row>
        <v-row>
          <qrcode-stream :camera="camera" @decode="onDecodeQR" >
            <div v-if="qrValidationPending" class="validation-pending">
            </div>
          </qrcode-stream>
        </v-row>
      </v-container>
    </v-dialog>
    <v-dialog v-model="showConnectionModal" max-width="600px" class="mx-auto">
      <ConnectionRequest
        :entity="connectionRequestEntity"
        :loading="connectionLoading"
        @cancel="cancelConnect"
        @accept="acceptConnect"
      ></ConnectionRequest>
    </v-dialog>
    <v-row no-gutters>
      <v-col cols="4" style="{border: 1px solid}">
        <v-divider></v-divider>
          <v-sheet class="pa-2 white">
            <v-text-field
              v-model="connectionsFilter"
              label="Search Connections"
              name="search"
              type="text"
              solo-inverted
              hide-details
              clearable
              clear-icon="mdi-close-circle-outline"
            ></v-text-field>
          </v-sheet>
        <v-btn
          class="ma-4"
          :loading="inviteSending"
          color="blue darken-1"
          text
          outlined
          @click="showInvite"
        >Invite</v-btn>
        <v-btn
          class="ma-4"
          color="blue darken-1"
          text
          outlined
          @click="showQRScan"
        >Scan QR</v-btn>
      <v-list two-line v-if="(invites.length != 0)">
        <v-divider></v-divider>
        <span class="pa-2 section-header">invites</span>
        <v-divider></v-divider>
        <v-list-item v-for="(invite, i) in invites" :key="i">
            <v-list-item-avatar color="accent">
              <v-img :src="`https://api.adorable.io/avatars/93/${invite.meta.email}`"></v-img>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ invite.meta.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ invite.meta.email }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-icon v-if="myInvite(invite)">
              <v-btn text>
                cancel
                <v-icon color="red" small right>fa-times-circle</v-icon>
              </v-btn>
            </v-list-item-icon>
            <v-list-item-icon v-else>
              <v-btn text @click="acceptInvite(invite)">
                accept
                <v-icon color="green" small right>fa-check-circle</v-icon>
              </v-btn>
            </v-list-item-icon>
        </v-list-item>
      </v-list>
      <v-list two-line>
        <v-divider></v-divider>
        <span class="pa-2 section-header">connections</span>
        <v-progress-linear v-if="connectionsLoading" indeterminate color="accent darken-2"></v-progress-linear>
        <v-divider></v-divider>
        <template v-for="(connection) in connectionList">
          <v-list-item :to="{name: 'connectionDetails', params: { 'conid': connection.doc.id }}" :key=connection.doc.id>
            <v-list-item-avatar color="accent" v-if="avatarImage(connection.entity)">
              <v-img :src="avatarImage(connection.entity)"></v-img>
            </v-list-item-avatar>
            <v-list-item-avatar color="accent" v-else>
              <span>{{ avatarLetters(connection.entity) }}</span>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ displayName(connection.entity) }}</v-list-item-title>
              <v-list-item-subtitle v-if="(connection.entity.type || 0) == 0">{{ primaryContactEmail(connection.entity) }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
      </v-col>
      <v-col>
        <v-divider></v-divider>
        <router-view :connections="connections" :connectionsLoading="connectionsLoading"></router-view>
      </v-col>
    </v-row>
     <v-snackbar
      v-model="inviteSnack"
      :timeout="1500"
      color="info">
      Invite copied to clipboard
    </v-snackbar>
  </v-container>
</template>

<script>
import base64url from 'base64url'
import QRCode from 'qrcode'
import { v4 as uuidv4 } from 'uuid'
import { mapGetters } from 'vuex'
import { QrcodeStream } from 'vue-qrcode-reader'
import ConnectionRequest from '@/components/ConnectionRequest'
import { avatarImage, avatarLetters, displayName, primaryContactEmail } from '@/utils/entityDisplay'

export default {
  name: 'connections',
  data () {
    return {
      connectionsFilter: '',
      inviteSending: false,
      showInviteModal: false,
      inviteEmail: '',
      inviteSnack: false,
      showScanModal: false,
      qrValidationPending: false,
      qrValidationError: '',
      camera: 'off',
      showConnectionModal: false,
      connectionRequestEntity: {},
      connectionLoading: false,
      connectionsLoading: true
    }
  },
  components: { QrcodeStream, ConnectionRequest },
  async mounted () {
    await this.$store.dispatch('hearro/getConnections')
    await this.$store.dispatch('hearro/getInvites')
    this.connectionsLoading = false
  },
  methods: {
    async sendInvite () {
      this.inviteSending = true
      try {
        await this.$store.dispatch('hearro/sendInvite', { invitee: this.inviteEmail })
        this.$store.dispatch('hearro/getInvites')
        this.showInviteModal = false
      } catch (e) {
        console.log(e)
      } finally {
        this.inviteSending = false
        this.connectionsFilter = ''
      }
    },
    myInvite (invite) {
      const { diddoc } = this.$store.getters['hearro/loggedInUser']
      const [agentKey] = diddoc.service
        .filter(element => element.id === '#agent')
        .map(e => e.recipient_keys[0])

      const myInvite = invite.data.connection.did_doc.service.find(element => {
        const myAgent = agentKey
        const mine = element.routing_keys.find(k => {
          return (k === myAgent)
        })
        return !!mine
      })
      return !!myInvite
    },
    async acceptInvite (invite) {
      try {
        await this.$store.dispatch('hearro/acceptInvite', { id: invite.data['@id'] })
        this.$store.dispatch('hearro/getConnections')
      } catch (e) {
        console.log('error accepting invite', e.message)
      }
    },
    async createConversation (conid) {
      const list = this.$store.getters['hearro/connections']
      const invitee = list.find(e => e.doc.id === conid)
      try {
        const id = await this.$store.dispatch('hearro/newConversation', { label: invitee.meta.name, participants: [invitee.doc.id] })
        console.log('new covnersation id', id)
      } catch (e) {
        console.log(e.message)
      }
    },
    showInvite () {
      this.showInviteModal = true
      this.$nextTick(() => {
        QRCode.toCanvas(document.getElementById('qrcanvas'), this.inviteLink(), (err, c) => {
          if (err) throw err
        })
      })
    },
    inviteLink () {
      const invite = {
        label: this.loggedInUser.user.email,
        did: ''
      }
      invite['@type'] = 'https://didcomm.org/didexchange/1.0/invitation'
      invite['@id'] = uuidv4()
      const invitePayload = base64url(JSON.stringify(invite))
      return `https://hearro.com/invites?c_i=${invitePayload}`
    },
    showQRScan () {
      this.showScanModal = true
      this.camera = 'auto'
    },
    async onDecodeQR (content) {
      this.validationPending = true
      this.camera = 'off'
      try {
        const qr = new URL(content)
        if (qr.hostname !== 'hearro.com' || !qr.searchParams.has('c_i')) {
          throw new Error('unable to handler url: not a valid invite')
        }
        const dataString = base64url.decode(qr.searchParams.get('c_i'))
        const data = JSON.parse(dataString)
        const reply = await this.$store.dispatch('hearro/resolveDid', { did: data.did })
        this.connectionRequestEntity = reply
        if (reply.entity.parent.length > 0) {
          try {
            const parent = await this.$store.dispatch('hearro/resolveDid', { did: reply.entity.parent })
            if (parent.entity) {
              this.connectionRequestEntity.entity.parentName = parent.entity.name.Label
            }
          } catch (e) {
            console.log(`could not resove parent ${e.message}`)
          }
        }
        this.showScanModal = false
        this.showConnectionModal = true
      } catch (e) {
        this.qrValidationError = e.message
      } finally {
        this.validationPending = false
      }
    },
    scanQRClose () {
      this.camera = 'off'
      this.validationPending = false
      this.qrValidationError = ''
    },
    cancelConnect () {
      this.showConnectionModal = false
      this.connectionRequestEntity = {}
    },
    async acceptConnect () {
      this.connectionLoading = true
      try {
        await await this.$store.dispatch('hearro/connectItem', { did: this.connectionRequestEntity.doc.id })
        this.showConnectionModal = false
      } catch (e) {
        console.log('error connecting', e.message)
      } finally {
        this.connectionLoading = false
      }
    },
    avatarImage (e) {
      return avatarImage(e)
    },
    avatarLetters (e) {
      return avatarLetters(e)
    },
    displayName (e) {
      return displayName(e)
    },
    primaryContactEmail (e) {
      return primaryContactEmail(e)
    },
    copyInvite () {
      const textArea = document.createElement('textarea')
      const invitePayload = base64url('{"@type": "https://didcomm.org/didexchange/1.0/invitation","@id":"asdf","label":"","did":""}')
      textArea.value = `https://hearro.com/invites?c_i=${invitePayload}`

      // Avoid scrolling to bottom
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.position = 'fixed';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        this.inviteSnackMsg = successful ? 'invite copited' : 'unable to copy to clipboard';
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }

      document.body.removeChild(textArea);
      this.inviteSnack = true
    }
  },
  computed: {
    ...mapGetters('hearro', ['loggedInUser', 'connections', 'invites']),
    connectionList () {
      const list = this.$store.getters['hearro/connections']
      return list.filter(e => {
        if (this.connectionsFilter === '') { return true }
        if ((e.meta.name.substring(0, this.connectionsFilter.length).toLowerCase() === this.connectionsFilter.toLowerCase()) ||
        (e.meta.email.substring(0, this.connectionsFilter.length).toLowerCase() === this.connectionsFilter.toLowerCase())) {
          return true
        }
      })
    }
  }
}
</script>

<style scoped>
.section-header {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}
.validation-pending {
  position: absolute;
  width: 100%;
  height: 100%;

  background-color: rgba(255, 255, 255, .8);
  text-align: center;
  font-weight: bold;
  font-size: 1.4rem;
  padding: 10px;

  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
}
</style>
